import React, { memo, useCallback, useEffect, useMemo, useState } from "react";
import CreatableSelect from "react-select/creatable";
import { Form, Modal } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faExclamationTriangle,
  faGripVertical,
  faMinusCircle,
  faPuzzlePiece,
  faPlusCircle,
  faStopCircle,
} from "@fortawesome/free-solid-svg-icons";
import { shallowEqual, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import Listener from "./Listener";
import checkErrors from "./helpers/checkErrors";
import Condition from "./Conditions/Condition";
import {
  selectEventHandlers,
  selectWorkflowEventListeners,
} from "../../../store/slices/projectSlice";
import HelpButton from "../../../HelpContent/HelpButton.tsx";
import { useConfirmDialog } from "../../Components/ConfirmDialogContext";

const getSelectedListenerStorageKey = ({ projectID, workflowUUID, event }) =>
  `logic-workflow-builder:selected-listener:${projectID}:${workflowUUID}:${event}`;

const getListenerCondition = (listenerState) =>
  listenerState?.ListenerCondition?.Condition ||
  listenerState?.ListenerCondition;

const isInteractiveElement = (element, container) => {
  const interactiveElement = element.closest(
    "button, input, select, textarea, a, [role='button'], [role='combobox'], [role='listbox'], [role='option']"
  );

  return interactiveElement && interactiveElement !== container;
};

const stopInteractiveEvent = (event) => {
  if (isInteractiveElement(event.target, event.currentTarget)) {
    event.stopPropagation();
  }
};

function ListenerTabs({ dispatch }) {
  const [showListeners, setShowListeners] = useState(false);
  const [showAddListenerName, setShowAddListenerName] = useState(false);
  const [newListenerName, setNewListenerName] = useState("");
  const [isNewListenerNameCreated, setIsNewListenerNameCreated] =
    useState(false);
  const [newListenerModuleUUID, setNewListenerModuleUUID] = useState(null);
  const [draggedListenerUUID, setDraggedListenerUUID] = useState(null);
  const [pendingCreatedListenerUUID, setPendingCreatedListenerUUID] =
    useState(null);
  const navigate = useNavigate();
  const confirmDialog = useConfirmDialog();
  const eventHandlers = useSelector(selectEventHandlers);
  const errors = useSelector(checkErrors, shallowEqual);

  const params = useParams();
  const { event, workflowUUID } = params;

  const eventListeners = useSelector(
    selectWorkflowEventListeners(workflowUUID)
  );
  const currentEventListeners = useMemo(
    () =>
      [...(eventListeners || [])]
        .filter((listener) => listener.EventType === event)
        .sort((a, b) => a.Order - b.Order),
    [event, eventListeners]
  );
  const eventHandlersByUUID = useMemo(
    () =>
      eventHandlers.reduce(
        (handlers, eventHandler) => ({
          ...handlers,
          [eventHandler.UUID]: eventHandler,
        }),
        {}
      ),
    [eventHandlers]
  );
  const moduleOptions = useMemo(
    () =>
      eventHandlers
        .filter((eventHandler) => eventHandler.IsModule)
        .map((eventHandler) => ({
          value: eventHandler.UUID,
          label: eventHandler.Label,
        })),
    [eventHandlers]
  );
  const listenerLabel = params.event === "form-render" ? "panel" : "listener";

  const setListener = useCallback(
    (data) =>
      dispatch({
        type: "update-listener",
        data,
      }),
    [dispatch]
  );

  const removeEventListener = useCallback(
    (listenerState) => {
      (async () => {
        const ans = await confirmDialog(
          "Delete listener",
          `Are you sure you want to delete this listener?`
        );
        if (ans === false) {
          return;
        }

        dispatch({
          type: "remove-listener",
          listener: listenerState,
          event: listenerState.EventType,
        });
        // navigate to the first listener
        const listenerUUID = eventListeners?.find(
          (l) =>
            l.EventType === event &&
            l.Order === 0 &&
            l.UUID !== listenerState.UUID
        )?.UUID;
        if (listenerUUID) {
          navigate(
            `/library/logic/builder/${params.projectID}/workflow/${params.workflowUUID}/event/${event}/listener/${listenerUUID}`
          );
        } else {
          navigate(
            `/library/logic/builder/${params.projectID}/workflow/${params.workflowUUID}/event/${event}`
          );
        }
      })();
    },
    [
      confirmDialog,
      dispatch,
      event,
      eventListeners,
      navigate,
      params.projectID,
      params.workflowUUID,
    ]
  );

  const listeners = currentEventListeners;

  const getListenerLabel = (listenerState) =>
    eventHandlersByUUID[listenerState.EventHandlerUUID]?.Label ||
    "New listener";

  const getEventHandler = (listenerState) =>
    eventHandlersByUUID[listenerState.EventHandlerUUID];

  const getCountLabel = (count, label) =>
    `${count} ${label}${count === 1 ? "" : "s"}`;

  const hasListenerErrors = (listenerState) =>
    errors.some((err) => err.eventListener?.UUID === listenerState.UUID);

  const setListenerCondition = useCallback(
    (listenerState, condition) => {
      const listenerConditionUUID =
        listenerState.ListenerCondition?.UUID ||
        condition.UUID ||
        `${listenerState.UUID}-listener-condition`;

      setListener({
        ...listenerState,
        ListenerCondition: {
          UUID: listenerConditionUUID,
          Condition: {
            ...condition,
            UUID: condition.UUID || listenerConditionUUID,
          },
        },
      });
    },
    [setListener]
  );

  const addListenerCondition = useCallback(
    (listenerState) => {
      const listenerConditionUUID = `${listenerState.UUID}-listener-condition`;

      setListener({
        ...listenerState,
        ListenerCondition: {
          UUID: listenerConditionUUID,
          Condition: {
            UUID: listenerConditionUUID,
            Options: [],
          },
        },
      });
    },
    [setListener]
  );

  const removeListenerCondition = useCallback(
    (listenerState) => {
      const { ListenerCondition, ...listener } = listenerState;
      setListener(listener);
    },
    [setListener]
  );

  const activeListener = listeners.find(
    (listener) => listener.UUID === params.listenerUUID
  );
  const hasListenersNeedingAttention = listeners.some(hasListenerErrors);

  const selectedListenerStorageKey = getSelectedListenerStorageKey({
    projectID: params.projectID,
    workflowUUID,
    event,
  });

  const navigateToListener = useCallback(
    (listenerUUID, options) => {
      navigate(
        `/library/logic/builder/${params.projectID}/workflow/${params.workflowUUID}/event/${params.event}/listener/${listenerUUID}`,
        options
      );
    },
    [navigate, params.event, params.projectID, params.workflowUUID]
  );

  const selectListener = useCallback(
    (listenerUUID) => {
      navigateToListener(listenerUUID);
    },
    [navigateToListener]
  );

  const openAddListenerName = useCallback(() => {
    setNewListenerName("");
    setIsNewListenerNameCreated(false);
    setNewListenerModuleUUID(null);
    setShowAddListenerName(true);
  }, []);

  const closeAddListenerName = useCallback(() => {
    setShowAddListenerName(false);
    setNewListenerName("");
    setIsNewListenerNameCreated(false);
    setNewListenerModuleUUID(null);
  }, []);

  const addListener = useCallback(() => {
    const label = newListenerName.trim();

    if (!label && !newListenerModuleUUID) {
      return;
    }

    const listenerUUID = dispatch({
      type: "add-listener",
      event,
      ...(newListenerModuleUUID
        ? { eventHandlerUUID: newListenerModuleUUID }
        : { label }),
    });
    if (listenerUUID) {
      setPendingCreatedListenerUUID(listenerUUID);
      navigateToListener(listenerUUID);
    }
    closeAddListenerName();
    setShowListeners(false);
  }, [
    closeAddListenerName,
    dispatch,
    event,
    navigateToListener,
    newListenerModuleUUID,
    newListenerName,
  ]);

  useEffect(() => {
    if (!params.projectID || !workflowUUID || !event) {
      return;
    }

    if (activeListener) {
      sessionStorage.setItem(selectedListenerStorageKey, activeListener.UUID);
      if (activeListener.UUID === pendingCreatedListenerUUID) {
        setPendingCreatedListenerUUID(null);
      }
      return;
    }

    if (pendingCreatedListenerUUID) {
      return;
    }

    const rememberedListenerUUID = sessionStorage.getItem(
      selectedListenerStorageKey
    );
    const listenerUUID =
      listeners.find((listener) => listener.UUID === rememberedListenerUUID)
        ?.UUID || listeners[0]?.UUID;

    if (listenerUUID && listenerUUID !== params.listenerUUID) {
      navigateToListener(listenerUUID, { replace: true });
    }
  }, [
    activeListener,
    event,
    listeners,
    pendingCreatedListenerUUID,
    params.listenerUUID,
    params.projectID,
    navigateToListener,
    selectedListenerStorageKey,
    workflowUUID,
  ]);

  useEffect(() => {
    if (
      params.conditionUUID &&
      listeners.some(
        (listener) =>
          getListenerCondition(listener)?.UUID === params.conditionUUID
      )
    ) {
      setShowListeners(true);
    }
  }, [listeners, params.conditionUUID]);

  const listenerSwitch = (
    <button
      type="button"
      className="btn btn-outline-secondary d-flex align-items-center"
      onMouseDown={(e) => e.stopPropagation()}
      onClick={() => setShowListeners(true)}
    >
      <span className="fw-bold me-1">
        {params.event === "form-render" ? "Panels" : "Listeners"}:
      </span>
      <span
        className="text-truncate"
        style={{ maxWidth: 220, marginRight: 10 }}
      >
        {activeListener ? getListenerLabel(activeListener) : "None selected"}
      </span>
      <span className="badge rounded-pill bg-light text-secondary ms-auto d-inline-flex align-items-center">
        {listeners.length}
      </span>
      {hasListenersNeedingAttention && (
        <FontAwesomeIcon
          icon={faExclamationTriangle}
          title="Listener needs attention"
          className="text-danger ms-2"
        />
      )}
    </button>
  );

  const listenerSelector = (
    <Modal
      show={showListeners}
      onHide={() => setShowListeners(false)}
      size="xxl"
      backdrop
      dialogClassName="mt-3"
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      <Modal.Header className="bg-colored-reverse text-white" closeButton>
        <Modal.Title>
          {params.event === "form-render" ? "Panels" : "Listeners"}
          <HelpButton
            helpKey="components-add-listener"
            btnClassName="text-white opacity-100"
            iconClassName="text-white"
          />
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="bg-page">
        <div
          className="list-group"
          style={{ maxHeight: 420, overflow: "auto" }}
        >
          {listeners.length === 0 ? (
            <div className="list-group-item text-muted">No listeners</div>
          ) : (
            listeners.map((listenerState) => {
              const isActive = listenerState.UUID === params.listenerUUID;
              const eventHandler = getEventHandler(listenerState);
              const hasErrors = hasListenerErrors(listenerState);
              const conditionsCount = (eventHandler?.Rules || []).reduce(
                (count, rule) => count + (rule.Conditions?.length || 0),
                0
              );
              const actionsCount = (eventHandler?.Rules || []).reduce(
                (count, rule) => count + (rule.Actions?.length || 0),
                0
              );

              return (
                <div
                  key={listenerState.UUID}
                  draggable
                  role="button"
                  tabIndex={0}
                  onDragStart={(e) => {
                    setDraggedListenerUUID(listenerState.UUID);
                    e.dataTransfer.effectAllowed = "move";
                  }}
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.dataTransfer.dropEffect = "move";
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    if (
                      draggedListenerUUID &&
                      draggedListenerUUID !== listenerState.UUID
                    ) {
                      dispatch({
                        type: "reorder-listeners",
                        event,
                        listenerId: draggedListenerUUID,
                        targetListenerId: listenerState.UUID,
                      });
                    }
                    setDraggedListenerUUID(null);
                  }}
                  onDragEnd={() => setDraggedListenerUUID(null)}
                  onClick={(e) => {
                    if (isInteractiveElement(e.target, e.currentTarget)) {
                      return;
                    }

                    selectListener(listenerState.UUID);
                  }}
                  onDoubleClick={(e) => {
                    if (isInteractiveElement(e.target, e.currentTarget)) {
                      return;
                    }

                    selectListener(listenerState.UUID);
                    setShowListeners(false);
                  }}
                  onKeyDown={(e) => {
                    if (isInteractiveElement(e.target, e.currentTarget)) {
                      return;
                    }

                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      selectListener(listenerState.UUID);
                    }
                  }}
                  className="list-group-item cursor-pointer"
                >
                  <div className="d-flex align-items-center gap-3 flex-nowrap">
                    <span style={{ width: 70 }} className="flex-shrink-0">
                      {isActive && (
                        <span className="badge rounded-pill bg-primary">
                          Selected
                        </span>
                      )}
                    </span>
                    <span
                      className="flex-shrink-0 text-start p-0 text-decoration-none d-flex align-items-center text-body"
                      style={{ width: 280 }}
                    >
                      <FontAwesomeIcon
                        icon={faGripVertical}
                        className="me-2 text-muted"
                      />
                      <span
                        className="text-truncate"
                        title={getListenerLabel(listenerState)}
                      >
                        {getListenerLabel(listenerState)}
                      </span>
                    </span>
                    <span className="d-inline-flex align-items-center gap-2 flex-shrink-0">
                      {eventHandler?.IsModule && (
                        <FontAwesomeIcon
                          icon={faPuzzlePiece}
                          title="Module"
                          className="text-primary"
                        />
                      )}
                      {listenerState.StopPropagation && (
                        <FontAwesomeIcon
                          icon={faStopCircle}
                          title="Stops propagation"
                          className="text-danger"
                        />
                      )}
                      {hasErrors && (
                        <FontAwesomeIcon
                          icon={faExclamationTriangle}
                          title="Contains errors"
                          className="text-danger"
                        />
                      )}
                      <span
                        className="badge rounded-pill bg-light text-secondary"
                        title={getCountLabel(conditionsCount, "condition")}
                      >
                        {getCountLabel(conditionsCount, "condition")}
                      </span>
                      <span
                        className="badge rounded-pill bg-light text-secondary"
                        title={getCountLabel(actionsCount, "action")}
                      >
                        {getCountLabel(actionsCount, "action")}
                      </span>
                    </span>
                    <div
                      className="d-flex align-items-center gap-2 flex-nowrap ms-auto"
                      onClick={stopInteractiveEvent}
                      onMouseDown={stopInteractiveEvent}
                    >
                      {getListenerCondition(listenerState) ? (
                        <>
                          <Condition
                            condition={getListenerCondition(listenerState)}
                            setCondition={(condition) =>
                              setListenerCondition(listenerState, condition)
                            }
                            index={0}
                            event={event}
                            allowedTypes={["variable:text", "number:compare"]}
                            compact
                          />
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-danger flex-shrink-0"
                            style={{ height: 31, width: 120 }}
                            onClick={() =>
                              removeListenerCondition(listenerState)
                            }
                          >
                            <FontAwesomeIcon icon={faMinusCircle} /> Remove
                          </button>
                        </>
                      ) : (
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-primary flex-shrink-0"
                          style={{ height: 31, width: 120 }}
                          onClick={() => addListenerCondition(listenerState)}
                        >
                          <FontAwesomeIcon icon={faPlusCircle} /> Add condition
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </Modal.Body>
      <Modal.Footer className="bg-page">
        <button
          type="button"
          className="btn btn-outline-primary btn-sm"
          onClick={openAddListenerName}
        >
          <FontAwesomeIcon icon={faPlusCircle} /> Add{" "}
          {params.event === "form-render" ? "panel" : "listener"}
        </button>
      </Modal.Footer>
    </Modal>
  );

  const addListenerNameModal = (
    <Modal
      show={showAddListenerName}
      onHide={closeAddListenerName}
      backdrop
      centered
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      <Form
        onSubmit={(e) => {
          e.preventDefault();
          addListener();
        }}
      >
        <Modal.Header className="bg-colored-reverse text-white" closeButton>
          <Modal.Title>
            New {params.event === "form-render" ? "panel" : "listener"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="bg-page">
          <Form.Group controlId="new-listener-name">
            <Form.Label>Name or module</Form.Label>
            <CreatableSelect
              menuPortalTarget={document.body}
              menuPlacement="auto"
              styles={{
                menu: (base) => ({
                  ...base,
                  width: "max-content",
                  minWidth: "100%",
                  boxShadow: "0px 0px 8px 0px #444",
                }),
                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
              }}
              autoFocus
              options={moduleOptions}
              placeholder="Select a module listener or start typing to create a new one"
              inputValue={
                newListenerModuleUUID || isNewListenerNameCreated
                  ? ""
                  : newListenerName
              }
              value={
                newListenerModuleUUID
                  ? moduleOptions.find(
                      (option) => option.value === newListenerModuleUUID
                    )
                  : isNewListenerNameCreated && newListenerName
                  ? { label: newListenerName, value: newListenerName }
                  : null
              }
              onChange={(option) => {
                setNewListenerName(option?.label || "");
                setIsNewListenerNameCreated(!!option?.__isNew__);
                setNewListenerModuleUUID(
                  option?.__isNew__ ? null : option?.value || null
                );
              }}
              onInputChange={(value, { action }) => {
                if (action === "input-change") {
                  setNewListenerName(value);
                  setIsNewListenerNameCreated(false);
                  setNewListenerModuleUUID(null);
                }
              }}
              onCreateOption={(value) => {
                setNewListenerName(value);
                setIsNewListenerNameCreated(true);
                setNewListenerModuleUUID(null);
              }}
              isValidNewOption={(value) => value.trim()}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer className="bg-page">
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            onClick={closeAddListenerName}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary btn-sm"
            disabled={!newListenerName.trim()}
          >
            Add {params.event === "form-render" ? "panel" : "listener"}
          </button>
        </Modal.Footer>
      </Form>
    </Modal>
  );

  return (
    <>
      <div className="d-flex align-items-center mb-3">{listenerSwitch}</div>
      {listenerSelector}
      {addListenerNameModal}
      <Listener
        setListener={setListener}
        removeEventListener={removeEventListener}
      />
      {!activeListener && (
        <div className="d-flex flex-column align-items-center justify-content-center text-center py-5 my-4">
          <FontAwesomeIcon
            icon={faExclamationTriangle}
            className="mb-3"
            style={{ color: "#cecece", fontSize: 250 }}
          />
          <button
            type="button"
            className="btn btn-outline-primary"
            onClick={openAddListenerName}
          >
            <FontAwesomeIcon icon={faPlusCircle} /> Add {listenerLabel}
          </button>
        </div>
      )}
    </>
  );
}

export default memo(ListenerTabs);
